import config from "../../../config/config.json";
import { apiCall } from "../../../_services/apiCall";

const BASE = `${config.API_URL}api/v1/ai-credits/`;

/**
 * Unwraps the standard envelope into the payload, or null on failure.
 *
 * @param {{isSuccess: boolean, data: object}} result - What apiCall returned.
 * @returns {object|null} The payload, or null when the call failed.
 *
 * @remarks
 * apiCall raises its own alert on failure, so the caller only needs to know
 * whether it has data. Returning null rather than throwing keeps every screen
 * from wrapping each read in a try/catch that would only re-report what the
 * user has already been shown.
 */
const payloadOf = (result) => (result?.isSuccess ? result.data?.data ?? null : null);

/**
 * Reads the rates currently in force.
 *
 * @returns {Promise<object|null>} Markup, credits-per-USD and FX freshness.
 */
export const fetchCreditConfig = async () => payloadOf(await apiCall(`${BASE}config`));

/**
 * Replaces the active rates.
 *
 * @param {object} body - Any of markup_percent, credits_per_usd, fx_max_age_minutes.
 * @returns {Promise<object|null>} The new active configuration.
 */
export const saveCreditConfig = async (body) =>
  payloadOf(await apiCall(`${BASE}config`, {}, body, "PUT"));

/**
 * Lists every granted discount with its company.
 *
 * @returns {Promise<object[]>} Discounts, newest first.
 */
export const fetchDiscounts = async () =>
  payloadOf(await apiCall(`${BASE}discounts`)) ?? [];

/**
 * Grants a discount to one company.
 *
 * @param {object} body - company_id, discount_percent, valid_from, valid_to.
 * @returns {Promise<object|null>} The created discount.
 */
export const createDiscount = async (body) =>
  payloadOf(await apiCall(`${BASE}discounts`, {}, body, "POST"));

/**
 * Amends an existing discount.
 *
 * @param {number} id - Discount id.
 * @param {object} body - Fields to change.
 * @returns {Promise<object|null>} The updated discount.
 */
export const updateDiscount = async (id, body) =>
  payloadOf(await apiCall(`${BASE}discounts/${id}`, {}, body, "PUT"));

/**
 * Withdraws a discount.
 *
 * @param {number} id - Discount id.
 * @returns {Promise<boolean>} Whether the call succeeded.
 */
export const deleteDiscount = async (id) => {
  const result = await apiCall(`${BASE}discounts/${id}`, {}, {}, "DELETE");
  return Boolean(result?.isSuccess);
};

/**
 * Lists companies a discount can be granted to.
 *
 * @returns {Promise<object[]>} Company accounts, each with an id and a name.
 *
 * @remarks
 * Read from the same endpoint the rest of the super-admin screens use rather
 * than a new one, so this page cannot show a different set of companies than
 * financial years or meters do.
 */
export const fetchCompanies = async () => {
  const result = await apiCall(
    `${config.ADMIN_API_URL}getCompanyList`,
    {},
    {},
    "GET"
  );

  return result?.isSuccess ? result.data?.data ?? [] : [];
};
